import { SHOW_CONSULT_ETA, SHOW_CONSULT_BAR } from "@iscr/constants";
import { useMemo } from 'react';
import { cls } from "@iscr/views/NUI/utils";
import { Link } from 'react-router';
import { useProfile } from '@iscr/xAppLib/Hooks';
import SIcon from '@iscr/views/NUI/StreamlineIcons/SIcon';
import { Progress } from "@iscr/components/ui/progress";
import { useOrderProgress } from 'views/ordering/hooks/useOrderProgress';
import { EstimatedWaitTime } from 'views/ordering/components/EstimatedWaitTime';
import moment from 'moment';
import { getClientBookingTimezone } from 'views/booking/shared';
import { FRIENDLY_DATETIME_DAY_MONTH } from 'views/booking/formatting';
import { checkWithinHours } from "@iscr/helpers/datetime";

/**
 * Renders an individual active consultation item with status, progress bar, and navigation
 * @param {Object} props
 * @param {Object} props.consult - The consultation data object
 * @param {string} props.clsn - Additional CSS classes
 */
function ActiveConsultItem({ consult, clsn }) {
	const { data } = useOrderProgress(consult.sid);

	const bookingTimeZone = getClientBookingTimezone();
	const time =
		moment(data?.appointment?.start_time).tz(bookingTimeZone)
			.format(FRIENDLY_DATETIME_DAY_MONTH);

	const isAppointment = data?.appointment?.active === true;
	const isPrescription = (data?.script_type === 'docscr' && data?.req_type === 'doccons') ||
		(['qualbuy', 'qualcons', 'qualclick', 'medcons', 'medbuy', 'medclick', 'exprcons', 'exprbuy', 'exprclick']
			.includes(data?.script_type) && ['premed'].includes(data?.req_type));
	const isPathoRequest = (!data?.is_script && ['pathoreq'].includes(data?.script_type));

	const statusText = data?.status_header || consult.status;
	const status = data?.status || consult.status;

	const awaitingConsult = ['doccons', 'qualcons', 'qualbuy', 'qualclick', 'medcons', 'medbuy', 'medclick', 'exprcons', 'exprbuy', 'exprclick'].includes(data?.script_type) &&
		['await_doccall', 'in_doccall', 'flagged_urgent', 'instcons_await', 'in_request', 'on_hold', 'wrong details', 'expr_declined', 'doc_declined', 'doc_approved'].includes(status);
	const awaitingMedCert = status === 'auto_approved_delay';
	const showEta = SHOW_CONSULT_ETA && !['in_doccall', 'done_doccall'].includes(status) && !isAppointment && !awaitingMedCert && !isPathoRequest;

	return (
		<div data-testid="card-active-consult" className={cls("bg-is2-green-750 lg:bg-white lg:rounded-lg lg:p-6 mb-0 lg:mx-0 relative ", clsn)} key={data?.sid}>
			{data?.med_db_data?.name && <div className="flex items-center min-h-[72px] lg:min-h-0 lg:mb-4 pr-[72px] pl-4 py-4 lg:p-0">
				<h2 className="text-white lg:text-is2-gray-700 text-left !text-is2_lg lg:!text-is2_xl !font-normal lg:!font-medium mb-0">
					Your <span className="hidden lg:inline-block">{!isAppointment ? 'active' : 'upcoming'}</span> {data?.med_db_data?.name}{isPrescription && " script request"}:
				</h2>
			</div>}
			<div className="flex flex-col space-y-4 bg-is2-green-800 lg:bg-is2-green-750 px-4 pt-4 pb-5 lg:p-4 lg:rounded-lg">
				<div className="flex flex-col lg:flex-row lg:mr-0 lg:items-center lg:justify-between">
					<div className="lg:w-[170px] xl:w-[186px] shrink-0">
						<p className="text-sm m-0 text-white text-left text-is2-green-50">Status:</p>
						<h4 className="mt-0 mb-0 !font-normal text-white !text-md text-left">
							{
								isAppointment
									? data?.status === 'done_doccall'
										? 'Finished'
										: 'Scheduled'
									: awaitingConsult
										? statusText
										: 'Awaiting Doctor review'
							}
						</h4>
					</div>
					<div className="flex flex-col justify-center text-left lg:px-4 [&>p]:!mt-4 lg:[&>p]:!mt-0 [&>p]:text-is2-green-50 [&>p]:text-sm lg:[&>p]:text-base">
						{showEta &&
						(
							<p>
								Estimated call time:{' '}
								<span className="text-white"><EstimatedWaitTime eta_minutes={data?.progress_eta?.max} /></span>
							</p>
						)}
						{awaitingMedCert && !isPathoRequest && !awaitingConsult && !isAppointment &&
							<p>Your certificate request will be reviewed in the next <span className="text-white"><EstimatedWaitTime eta_minutes={10} /></span></p>
						}
						{isPathoRequest && !isAppointment &&
							<p>A doctor will review your test request shortly.</p>
						}
						{isAppointment &&
							<p>
								Appointment on <strong>{time}</strong> <small>({bookingTimeZone}) </small>
							</p>
						}
					</div>
					<Link to={`order/${consult.sid}`} className="h-fit absolute lg:static right-4 top-4 lg:translate-y-0">
						<div className={cls("bg-white text-is2-gray-body rounded-full w-[40px] h-[40px] flex items-center justify-center cursor-pointer -rotate-[90deg]")}>
							<SIcon name="chevron" size="xxs" bold />
						</div>
					</Link>
				</div>
				
				{showEta && (
					<>
					{SHOW_CONSULT_BAR && 
						<div className="w-full">
							<Progress
								value={data?.progress}
								showDot={true}
								className="h-5 z-5"
								fg="hsl(var(--progress-active-consultation-foreground))"
								bg="hsl(var(--progress-active-consultation-background))"
							/>
						</div>
					}
					{(checkWithinHours(data?.add_tm, 7, 19) && data?.progress_eta?.max > 15) &&
						<p className="text-left text-sm text-is2-green-100 border-t border-is2-blue-50 pt-3 mb-3 lg:pt-4 lg:mt-4 w-full">You will receive an SMS when you are nearing the top of the call queue.</p>
					}
				</>
				)}
			</div>
		</div>
	);
}

/**
 * Displays active consultations from user's history
 * Filters for:
 * - Consultations within last 2 months
 * - Specific consultation types (doccons, qualcons, qualbuy and qualclick.)
 * - Active statuses (await_doccall, in_doccall)
 * - Pathology requests, medical certificates, appointments and doctor referrals
 * 
 * @param {Object} props
 * @param {string} [props.clsn=""] - Optional CSS class
 * @param {number} [props.limit] - Optional limit on number of consultations to display
 * @param {number} [props.skip=0] - Optional number of consultations to skip from the beginning
 * @returns {JSX.Element[]} Array of ActiveConsultItem components
 */
export function ActiveConsult({ clsn = "", limit = 0, skip = 0 }) {
	const [prof] = useProfile();

	const active = useMemo(() => {
		const twoMonthsAgo = moment().subtract(2, 'months');

		const filteredConsults = (prof?.hist ?? []).filter((r) => {
			const consultDate = moment(r.add_tm, 'D/M/YY HH:mm:ss');
			const isRecent = consultDate.isAfter(twoMonthsAgo);

			return isRecent && (
				['await_doccall', 'in_doccall', 'flagged_urgent', 'instcons_await', 'noans_doccall', 'in_request', 'on_hold', 'need_review'].includes(r.status)
				|| (r.script_type === 'exprcons' && ['medcert', 'xc_patho'].includes(r.req_type) && ['await_doccall'].includes(r.status)) // new express consults for certs and patho
				|| (!r.is_script && ['pathoreq'].includes(r.script_type))
				|| ['medcert'].includes(r.req_type) && !['doc_approved'].includes(r.status)
				|| (!r.is_script && ['docrefr'].includes(r.script_type))
			);
		});

		const processedConsults = filteredConsults.slice(skip);
		const limitedConsults = limit > 0 ? processedConsults.slice(0, limit) : processedConsults;

		return limitedConsults.map(consult => ({
			...consult,
			appointment: prof?.pts_appointments?.find(appt => appt.sid === consult.sid) || null
		}));
	}, [prof, limit, skip]);

	return active.map(consult => (
		<ActiveConsultItem key={consult.sid} consult={consult} clsn={clsn} />
	));
}
